/**
 * Homepage copy and imagery, per brand.
 *
 * Taken verbatim from the Figma homepage frames (2021:2181 home, 2021:2564
 * business). The two pages share a structure and differ only in content and
 * palette, so they are one component driven by this table rather than two
 * near-identical pages that drift apart.
 */
import { Handshake, PoundSterling, ShieldCheck, Zap } from '../components/icons.jsx';

export const HOME_CONTENT = {
  home: {
    /** Where the hero search sends the customer. */
    journeySlug: 'home',
    hero: {
      title: 'Find Your Perfect Deal',
      subtitle: 'Start your search for better home broadband today!',
      image: '/brand/home/hero-person.webp',
    },
    benefits: {
      eyebrow: 'Switching Benefits',
      title: 'Why switch your broadband with us?',
      subtitle:
        'We make comparing and switching broadband packages completely effortless, helping you secure faster connections for lower monthly prices.',
      cards: [
        {
          icon: PoundSterling,
          title: 'Save Money',
          body: 'Stop overpaying. Compare hundreds of packages instantly to find the cheapest tariffs and save up to £280 a year on your bills.',
        },
        {
          icon: Zap,
          title: 'Faster Speeds',
          body: 'Upgrade to full-fibre broadband with hyperfast downloads up to 1Gbps, perfect for busy households and endless downloads.',
        },
        {
          icon: ShieldCheck,
          title: 'Better Reliability',
          body: 'Say goodbye to dropouts and pixelated video calls with next-generation FTTP lines that keep your entire home constantly connected.',
        },
        {
          icon: Handshake,
          title: 'No Hassle Switch',
          body: 'We handle the entire process. Our direct switching guarantee means your service transitions seamlessly with absolutely zero downtime.',
        },
      ],
    },
    bento: {
      eyebrow: 'Upgrade your home network',
      title: 'Home broadband for every need',
      subtitle:
        'From intense online multiplayer lobbies to streaming beautiful movies in stunning 4K, find the perfect speed profile calibrated for your lifestyle.',
      // `wide` marks the two tiles on the second row, which are half-width
      // rather than thirds.
      tiles: [
        {
          image: '/brand/home/streaming-4k.webp',
          title: 'Streaming in 4K',
          body: 'Enjoy buffer-free movies and television on every display in your home simultaneously.',
        },
        {
          image: '/brand/home/pro-gaming.webp',
          title: 'Pro-Level Gaming',
          body: 'Ultra-low latency connection built for competitive multiplayer with zero packet loss.',
        },
        {
          image: '/brand/home/working-from-home.webp',
          title: 'Working From Home',
          body: 'Flawless HD video calls, instant file uploads, and secure cloud VPN connections.',
        },
        {
          image: '/brand/home/smart-home.webp',
          title: 'Connected Smart Home',
          body: 'Support dozens of intelligent smart devices running smoothly together with zero performance hits.',
          wide: true,
        },
        {
          image: '/brand/home/family-bandwidth.webp',
          title: 'Multi-User Family Bandwidth',
          body: 'Abundant internet bandwidth for homework, streaming, streaming devices, and gaming all at once.',
          wide: true,
        },
      ],
    },
    cta: 'Start Your Switch Today',
  },

  business: {
    journeySlug: 'business',
    hero: {
      title: 'Find The Right Business Broadband.',
      // The Figma tints just this word in the headline.
      highlight: 'Business',
      subtitle:
        'Business broadband deals from the UK’s leading providers. Dedicated lines, guaranteed speeds, and priority support.',
      image: '/brand/business/hero-person.webp',
    },
    benefits: {
      eyebrow: 'Switching Benefits',
      title: 'Why switch your business broadband with us?',
      subtitle:
        'We help companies replace sluggish residential lines with high-reliability business connectivity tailored for commercial growth.',
      cards: [
        {
          icon: PoundSterling,
          title: 'Cut Costs',
          body: 'Reduce overheads with highly competitive business tariffs and exclusive multi-site bundle discounts.',
        },
        {
          icon: Zap,
          title: 'Dedicated Lines',
          body: 'Guaranteed symmetric bandwidth not shared with residential consumers for uncontended performance.',
        },
        {
          icon: ShieldCheck,
          title: 'Priority Support',
          body: 'Access round-the-clock 24/7 business-grade technical support with rapid on-site engineer dispatch.',
        },
        {
          icon: Handshake,
          title: 'No Hassle Switch',
          body: 'We handle the entire process. Our switching guarantee means your service transitions seamlessly with no downtime.',
        },
      ],
    },
    bento: {
      eyebrow: 'Upgrade your Business network',
      title: 'Business Broadband For Every Need',
      subtitle:
        'Scale your operations seamlessly with tailored connectivity engineered specifically for your commercial workflows.',
      tiles: [
        {
          image: '/brand/business/video-conferencing.webp',
          title: 'Video Conferencing',
          body: 'Crystal-clear, lag-free virtual meetings for distributed teams and key client presentations.',
        },
        {
          image: '/brand/business/cloud-computing.webp',
          title: 'Cloud Computing',
          body: 'Instant, secure backup and real-time syncing across standard cloud platforms and SaaS architectures.',
        },
        {
          image: '/brand/business/multi-site.webp',
          title: 'Multi-Site Connectivity',
          body: 'Seamlessly bridge office headquarters, remote logistics hubs, and physical retail fronts.',
        },
      ],
    },
    cta: 'Upgrade Your Business Connectivity Today',
  },
};

export const SEARCH_PLACEHOLDER = 'Tell us your postcode to get started....';
